import { type FC, useState } from 'react';
import { type CreateClientData, createClient } from '@/api/clients';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface NewClientFormData {
  name: string;
  client_code: string;
  company_email: string;
  keycloak_group: string;
}

const initialFormData: NewClientFormData = {
  name: '',
  client_code: '',
  company_email: '',
  keycloak_group: '',
};

interface FormErrors {
  name?: string;
  client_code?: string;
  company_email?: string;
  keycloak_group?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Matches backend (Zod .email()): requires TLD of at least 2 chars, e.g. .com not .c
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function getClientCodeError(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  if (/[^A-Z0-9]/.test(v)) return 'Only uppercase letters and numbers allowed';
  if (v.length < 3) return 'Must be at least 3 characters';
  return null;
}

function validateForm(data: NewClientFormData): FormErrors {
  const errors: FormErrors = {};

  if (!data.name.trim()) {
    errors.name = 'Client name is required';
  } else if (data.name.trim().length > 255) {
    errors.name = 'Client name must be 255 characters or fewer';
  }

  const code = data.client_code.trim();
  if (!code) {
    errors.client_code = 'Client code is required';
  } else if (/[^A-Z0-9]/.test(code)) {
    errors.client_code = 'Only uppercase letters and numbers allowed';
  } else if (code.length < 3) {
    errors.client_code = 'Must be at least 3 characters';
  }

  if (data.company_email.trim() && !EMAIL_REGEX.test(data.company_email.trim())) {
    errors.company_email = 'Please enter a valid email address';
  }

  if (data.keycloak_group.trim().length > 255) {
    errors.keycloak_group = 'SFTP Group must be 255 characters or fewer';
  }

  return errors;
}

export const NewClientModal: FC<Props> = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<NewClientFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateFormData = (field: keyof NewClientFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'client_code') {
      const codeError = getClientCodeError(value);
      setFormErrors((prev) => {
        const next = { ...prev };
        if (codeError) {
          next.client_code = codeError;
        } else {
          delete next.client_code;
        }
        return next;
      });
    } else if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);
    setError(null);
    setFormErrors({});

    try {
      const payload: CreateClientData = {
        name: formData.name.trim(),
        client_code: formData.client_code.trim().toUpperCase(),
      };

      const emailValue = formData.company_email.trim();
      if (emailValue) {
        payload.company_email = emailValue;
      }

      const keycloakGroupValue = formData.keycloak_group.trim();
      if (keycloakGroupValue) {
        payload.keycloak_group = keycloakGroupValue;
      }

      await createClient(payload);

      // Reset form and close modal
      setFormData(initialFormData);
      onSuccess();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create client');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData(initialFormData);
      setFormErrors({});
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Client</DialogTitle>
          <DialogDescription>Create a new client profile</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Server Error Display */}
          {error && (
            <div className="bg-destructive/15 border border-destructive text-destructive px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="grid gap-4">
            {/* Client Name */}
            <div className="space-y-2">
              <Label htmlFor="client-name">
                Client Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="client-name"
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
                placeholder="Enter client name"
                aria-invalid={!!formErrors.name}
                autoFocus
              />
              {formErrors.name && <p className="text-sm text-destructive">{formErrors.name}</p>}
            </div>

            {/* Client Code */}
            <div className="space-y-2">
              <Label htmlFor="client-code">
                Client Code <span className="text-destructive">*</span>
              </Label>
              <Input
                id="client-code"
                value={formData.client_code}
                onChange={(e) =>
                  updateFormData(
                    'client_code',
                    e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
                  )
                }
                placeholder="e.g. ACME01"
                maxLength={10}
                aria-invalid={!!formErrors.client_code}
              />
              {formErrors.client_code && (
                <p className="text-sm text-destructive">{formErrors.client_code}</p>
              )}
              <p className="text-xs text-muted-foreground">
                3–10 uppercase letters and numbers. Must be unique across clients.
              </p>
            </div>

            {/* Company Email */}
            <div className="space-y-2">
              <Label htmlFor="client-email">Company Email</Label>
              <Input
                id="client-email"
                type="email"
                value={formData.company_email}
                onChange={(e) => updateFormData('company_email', e.target.value)}
                placeholder="company@example.com"
                aria-invalid={!!formErrors.company_email}
              />
              {formErrors.company_email && (
                <p className="text-sm text-destructive">{formErrors.company_email}</p>
              )}
            </div>

            {/* SFTP Group (Keycloak group path) */}
            <div className="space-y-2">
              <Label htmlFor="client-keycloak-group">SFTP Group</Label>
              <Input
                id="client-keycloak-group"
                value={formData.keycloak_group}
                onChange={(e) => updateFormData('keycloak_group', e.target.value)}
                placeholder="e.g. /sftp/clients/acme"
                aria-invalid={!!formErrors.keycloak_group}
              />
              {formErrors.keycloak_group && (
                <p className="text-sm text-destructive">{formErrors.keycloak_group}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Optional. Used for SFTP upload routing; must be unique across clients.
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isLoading || !formData.name.trim() || formData.client_code.trim().length < 3
              }
            >
              {isLoading ? 'Creating...' : 'Create Client'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
